import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const CallCenterApi = createApi({
  reducerPath: "CallCenter",
  baseQuery: fetchBaseQuery({
    baseUrl: `${process.env.REACT_APP_BACKEND3_URL}/call-center`,
    credentials: "include",
    prepareHeaders: (headers) => {
      const token = localStorage.getItem("token");
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
      // Backend resolves the response language from this header (see
      // backend3 middleware/localization.ts) — without it every endpoint
      // silently falls back to English regardless of the app's actual
      // language toggle, since the browser's own Accept-Language reflects
      // OS/browser locale, not localStorage("lang").
      headers.set("Accept-Language", localStorage.getItem("lang") === "ar" ? "ar" : "en");
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getModelsByProduct: builder.query({
      query: (productId) =>
        productId
          ? `/models?productId=${productId}&dropdown=true`
          : `/models?dropdown=true`,
    }),
    getIssuesByModel: builder.query({
      query: ({ modelId, lang }) => `/issues?modelId=${modelId}&lang=${lang}`,
    }),
    getSparePartsByIssues: builder.query({
      query: ({ issueIds, lang }) => `/spare-parts?issueIds=${issueIds.join(",")}&lang=${lang}`,
    }),
    lookupCustomerByPhone: builder.query({
      query: (phone) => `/customer-lookup?phone=${encodeURIComponent(phone)}`,
    }),
    createCallCenterRequest: builder.mutation({
      query: (payload) => ({
        url: "/service-requests",
        method: "POST",
        body: payload,
      }),
    }),
    createIssue: builder.mutation({
      query: (payload) => ({
        url: "/issues",
        method: "POST",
        body: payload,
      }),
    }),
    createSparePart: builder.mutation({
      query: (payload) => ({
        url: "/spare-parts",
        method: "POST",
        body: payload,
      }),
    }),
    getVillagesByMarkaz: builder.query({
      // governorate/markaz are optional (omitting them lists every village —
      // used by Dispatcher's Village filter); existing callers that always
      // pass both keep their exact same request.
      query: ({ governorate, markaz, lang } = {}) => {
        const params = new URLSearchParams();
        if (governorate) params.set("governorate", governorate);
        if (markaz) params.set("markaz", markaz);
        if (lang) params.set("lang", lang);
        const qs = params.toString();
        return `/villages${qs ? `?${qs}` : ""}`;
      },
    }),
    createVillage: builder.mutation({
      query: (payload) => ({
        url: "/villages",
        method: "POST",
        body: payload,
      }),
    }),
    getServiceHistory: builder.query({
      query: (poiId) => `/service-history?poiId=${poiId}`,
    }),
    getServiceRequestForEdit: builder.query({
      query: (id) => `/service-requests/${id}`,
    }),
    updateServiceRequest: builder.mutation({
      query: ({ id, payload }) => ({
        url: `/service-requests/${id}`,
        method: "PUT",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetModelsByProductQuery,
  useGetIssuesByModelQuery,
  useGetSparePartsByIssuesQuery,
  useLazyLookupCustomerByPhoneQuery,
  useCreateCallCenterRequestMutation,
  useCreateIssueMutation,
  useCreateSparePartMutation,
  useGetVillagesByMarkazQuery,
  useCreateVillageMutation,
  useLazyGetServiceHistoryQuery,
  useGetServiceRequestForEditQuery,
  useLazyGetServiceRequestForEditQuery,
  useUpdateServiceRequestMutation,
} = CallCenterApi;
